import { useCallback, useRef } from 'react';

import { Timeout } from 'src/types';

export interface Dimensions {
  width: number;
  height: number;
  top: number;
  left: number;
}

export const defaultDimensions: Dimensions = {
  width: 0,
  height: 0,
  top: 0,
  left: 0,
};

export interface UseDimensionsReturn {
  observe: (node: HTMLDivElement | null) => void;
  resizeFinished: () => Promise<void>;
}

export const useOnResize = (
  fn: (value: Dimensions) => void,
  resizingDelayMs: number = 50,
): UseDimensionsReturn => {
  const resizing = useRef<Promise<void> | null>(null);
  const resizingTimeout = useRef<Timeout | null>(null);
  const resolver = useRef<null | (() => void)>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const createResizingTimeout = useCallback(() => {
    resizingTimeout.current = setTimeout(() => {
      resolver.current?.();
      resizing.current = null;
      resolver.current = null;
      resizingTimeout.current = null;
    }, resizingDelayMs);
  }, [resizingDelayMs]);

  const observe = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        observerRef.current?.disconnect();
        return;
      }

      const initial = node.getBoundingClientRect();
      fn(initial);

      observerRef.current = new ResizeObserver(entries => {
        const { left, top } = node.getBoundingClientRect();

        if (resizingTimeout.current) {
          clearTimeout(resizingTimeout.current);
          createResizingTimeout();
        }

        if (!resizing.current) {
          resizing.current = new Promise(res => {
            resolver.current = res;
            createResizingTimeout();
          });
        }

        for (const entry of entries) {
          const result = {
            width: entry.contentRect.width,
            height: entry.contentRect.height,
            left,
            top,
          };

          fn(result);
        }
      });

      observerRef.current.observe(node);
    },
    [createResizingTimeout, fn],
  );

  const resizeFinished = useCallback(async (): Promise<void> => {
    return new Promise(res => {
      setTimeout(async () => {
        if (resizing.current) {
          await resizing.current;
        }

        res();
      }, resizingDelayMs * 2);
    });
  }, [resizingDelayMs]);

  return { observe, resizeFinished };
};
